import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useCallback } from 'react';
import { ResponsiveGridLayout, useContainerWidth, verticalCompactor } from 'react-grid-layout';
import WidgetRenderer from './WidgetRenderer';
import DrillThrough from './DrillThrough';
function summarizeQuery(widget) {
    try {
        const qc = JSON.parse(widget.queryConfig);
        if (qc.mode === 'sql' && qc.query)
            return String(qc.query);
        // Builder mode — build a human-readable summary
        const parts = [];
        const agg = qc.aggregation;
        const aggCol = qc.aggregationColumn;
        const cols = qc.columns;
        const table = qc.tableName;
        if (agg && aggCol)
            parts.push(`SELECT ${agg.toUpperCase()}("${aggCol}") as value`);
        else if (agg)
            parts.push(`SELECT COUNT(*) as value`);
        else if (cols === null || cols === void 0 ? void 0 : cols.length)
            parts.push(`SELECT ${cols.map(c => `"${c}"`).join(', ')}`);
        else
            parts.push('SELECT *');
        if (table)
            parts.push(`FROM "${table}"`);
        const gb = qc.groupBy;
        const gbList = Array.isArray(gb) ? gb : gb ? [gb] : [];
        if (gbList.length > 0)
            parts.push(`GROUP BY ${gbList.map(c => `"${c}"`).join(', ')}`);
        const ob = qc.orderBy;
        if (ob)
            parts.push(`ORDER BY ${ob}`);
        const lim = qc.limit;
        if (lim)
            parts.push(`LIMIT ${lim}`);
        return parts.join('\n');
    }
    catch (_a) {
        return 'Unable to parse query config';
    }
}
const DashboardCanvas = ({ widgets, widgetData, layout, pages, activePage: _activePage, onLayoutChange, onEditWidget, onDeleteWidget, onAssignWidgetPage, onDrillThrough }) => {
    const { width, containerRef } = useContainerWidth();
    const [showQuery, setShowQuery] = useState(new Set());
    const [pageDropdownOpen, setPageDropdownOpen] = useState(null);
    const [drillState, setDrillState] = useState(null);
    const handleDrillThrough = useCallback((widgetId, widgetName, record) => {
        const filters = Object.entries(record).map(([column, value]) => ({
            column, tableName: '', kind: 'text', values: [String(value)]
        }));
        setDrillState({ widgetId, widgetName, filters });
        onDrillThrough === null || onDrillThrough === void 0 ? void 0 : onDrillThrough(widgetId, widgetName, record);
    }, [onDrillThrough]);
    const toggleQuery = (id) => setShowQuery(prev => {
        const next = new Set(prev);
        if (next.has(id))
            next.delete(id);
        else
            next.add(id);
        return next;
    });
    // Close page dropdown when clicking outside
    React.useEffect(() => {
        if (!pageDropdownOpen)
            return;
        const handler = () => setPageDropdownOpen(null);
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [pageDropdownOpen]);
    // Map widgetSize from chartConfig to grid dimensions
    const sizeMap = {
        small: { w: 3, h: 3 },
        medium: { w: 4, h: 4 },
        large: { w: 6, h: 5 },
        full: { w: 12, h: 5 },
    };
    // Generate default layout for widgets without one
    const effectiveLayout = [];
    for (let i = 0; i < widgets.length; i++) {
        const w = widgets[i];
        const existing = layout.find(l => l.i === w.id);
        if (existing) {
            effectiveLayout.push(existing);
            continue;
        }
        // Read widgetSize from chartConfig
        let size = sizeMap.medium;
        try {
            const cc = JSON.parse(w.chartConfig);
            if (cc.widgetSize && sizeMap[cc.widgetSize]) {
                size = sizeMap[cc.widgetSize];
            }
        }
        catch ( /* ignore */_a) { /* ignore */ }
        // Auto-position: pack left-to-right, wrapping at col 12
        let x = 0, y = 0;
        if (i > 0) {
            const prev = effectiveLayout[i - 1];
            x = prev.x + prev.w;
            y = prev.y;
            if (x + size.w > 12) {
                x = 0;
                y = prev.y + prev.h;
            }
        }
        effectiveLayout.push({ i: w.id, x, y, w: size.w, h: size.h, minW: 2, minH: 2 });
    }
    if (widgets.length === 0) {
        return (_jsx("div", { className: "d-flex align-items-center justify-content-center text-muted h-100", children: _jsxs("div", { className: "text-center", children: [_jsx("i", { className: "bi bi-bar-chart-line", style: { fontSize: '3rem' } }), _jsx("p", { className: "mt-2", children: "No widgets yet. Click \"Add Widget\" to get started." })] }) }));
    }
    return (_jsxs("div", { ref: containerRef, style: { height: '100%' }, children: [width > 0 && (_jsx(ResponsiveGridLayout, { className: "layout", width: width, layouts: { lg: effectiveLayout }, breakpoints: { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }, cols: { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }, rowHeight: 60, onLayoutChange: (newLayout) => onLayoutChange(newLayout), dragConfig: { enabled: true, handle: '.widget-drag-handle' }, compactor: verticalCompactor, children: widgets.map(w => {
                    const wd = widgetData[w.id] || { data: null, loading: true, error: null };
                    return (_jsxs("div", { className: "border rounded bg-white shadow-sm d-flex flex-column", children: [_jsxs("div", { className: "widget-drag-handle d-flex justify-content-between align-items-center px-2 py-1 border-bottom bg-light", style: { cursor: 'grab', minHeight: 32 }, children: [_jsx("span", { className: "small fw-medium text-truncate", children: w.name }), _jsxs("div", { className: "d-flex gap-1", children: [_jsx("button", { className: "btn btn-sm p-0 text-muted", style: { lineHeight: 1 }, onClick: () => toggleQuery(w.id), title: "View Query", "aria-label": "View query", children: _jsx("i", { className: `bi bi-code-slash ${showQuery.has(w.id) ? 'text-primary' : ''}`, style: { fontSize: '0.75rem' } }) }), onAssignWidgetPage && pages && pages.length > 0 && (_jsxs("div", { className: "position-relative", style: { lineHeight: 1 }, children: [_jsx("button", { className: "btn btn-sm p-0 text-muted", style: { lineHeight: 1 }, onClick: e => { e.stopPropagation(); setPageDropdownOpen(pageDropdownOpen === w.id ? null : w.id); }, title: "Move to page", "aria-label": "Move to page", children: _jsx("i", { className: "bi bi-folder-symlink", style: { fontSize: '0.75rem' } }) }), pageDropdownOpen === w.id && (_jsxs("ul", { className: "dropdown-menu show", style: { position: 'absolute', top: '100%', right: 0, zIndex: 1050, minWidth: 140 }, children: [_jsx("li", { children: _jsx("button", { className: `dropdown-item small${w.pageId === null || w.pageId === '' ? ' active' : ''}`, onClick: () => { onAssignWidgetPage(w.id, null); setPageDropdownOpen(null); }, children: "Overview" }) }), pages.map(p => (_jsx("li", { children: _jsx("button", { className: `dropdown-item small${w.pageId === p.id ? ' active' : ''}`, onClick: () => { onAssignWidgetPage(w.id, p.id); setPageDropdownOpen(null); }, children: p.title }) }, p.id)))] }))] })), _jsx("button", { className: "btn btn-sm p-0 text-muted", style: { lineHeight: 1 }, onClick: () => onEditWidget(w), title: "Edit", "aria-label": "Edit widget", children: _jsx("i", { className: "bi bi-pencil", style: { fontSize: '0.75rem' } }) }), _jsx("button", { className: "btn btn-sm p-0 text-muted", style: { lineHeight: 1 }, onClick: () => onDeleteWidget(w.id), title: "Delete", "aria-label": "Delete widget", children: _jsx("i", { className: "bi bi-trash", style: { fontSize: '0.75rem' } }) })] })] }), _jsx("div", { className: "flex-grow-1 p-1 position-relative", style: { minHeight: 0, overflow: 'hidden' }, children: showQuery.has(w.id) ? (_jsx("pre", { className: "m-0 p-2 h-100 small font-monospace text-body-secondary", style: {
                                        overflow: 'auto', fontSize: '0.7rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                        background: 'var(--bs-tertiary-bg, #f8f9fa)', borderRadius: '0 0 0.375rem 0.375rem'
                                    }, children: summarizeQuery(w) })) : (_jsx(WidgetRenderer, { widget: w, data: wd.data, loading: wd.loading, error: wd.error, onDrillThrough: (filters) => handleDrillThrough(w.id, w.name, filters) })) })] }, w.id));
                }) })), drillState && (_jsx(DrillThrough, { isOpen: true, onClose: () => setDrillState(null), widgetId: drillState.widgetId, widgetName: drillState.widgetName, drillFilters: drillState.filters }))] }));
};
export default DashboardCanvas;
